import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ImageCropper } from "./ImageCropper";
import { useStoreImageUpload } from "@/hooks/useStoreImageUpload";

interface StoreImageUploadProps {
  storefrontImageUrl: string | null;
  coverImageUrl: string | null;
  useStorefrontAsCover: boolean;
  onStorefrontImageChange: (url: string | null) => void;
  onCoverImageChange: (url: string | null) => void;
  onUseStorefrontAsCoverChange: (value: boolean) => void;
  disabled?: boolean;
}

// Aspect ratios based on the FloristProfile display
const PROFILE_ASPECT_RATIO = 1; // Square (1:1) for profile image
const COVER_ASPECT_RATIO = 16 / 9; // Widescreen (16:9) for cover image

export function StoreImageUpload({
  storefrontImageUrl,
  coverImageUrl,
  useStorefrontAsCover,
  onStorefrontImageChange,
  onCoverImageChange,
  onUseStorefrontAsCoverChange,
  disabled = false
}: StoreImageUploadProps) {
  const { uploadImage, deleteImage, isUploading } = useStoreImageUpload();
  const [storefrontPreview, setStorefrontPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [isStorefrontLoading, setIsStorefrontLoading] = useState(false);
  const [isCoverLoading, setIsCoverLoading] = useState(false);
  
  // Cropper states
  const [cropperImage, setCropperImage] = useState<string | null>(null);
  const [cropperType, setCropperType] = useState<'profile' | 'cover' | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const [tempFileType, setTempFileType] = useState<string>('image/jpeg');
  
  // Set previews from props
  useEffect(() => {
    if (storefrontImageUrl) setStorefrontPreview(storefrontImageUrl);
    if (coverImageUrl) setCoverPreview(coverImageUrl);
  }, [storefrontImageUrl, coverImageUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Reset input value
    e.target.value = '';
    
    // Store the file for later upload after cropping
    setTempFile(file);
    setTempFileType(file.type || 'image/jpeg');
    
    // Read the file for preview in the cropper
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setCropperImage(reader.result);
        setCropperType(type);
        setShowCropper(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!tempFile || !cropperType) return;
    
    try {
      // Get the MIME type from the data URL
      const mimeType = croppedImage.split(';')[0].split(':')[1];
      
      // Convert the base64 string to a Blob with proper type
      const blobBin = atob(croppedImage.split(',')[1]);
      const array = [];
      for (let i = 0; i < blobBin.length; i++) {
        array.push(blobBin.charCodeAt(i));
      }
      
      // Use the original file's type when creating the Blob
      const fileData = new Blob([new Uint8Array(array)], { type: mimeType });
      
      // Create a file with original name but the cropped data
      const croppedFile = new File(
        [fileData], 
        tempFile.name, 
        { 
          type: mimeType,
          lastModified: new Date().getTime()
        }
      );
      
      if (cropperType === 'profile') {
        setIsStorefrontLoading(true);
        const url = await uploadImage(croppedFile, 'storefront');
        if (url) {
          setStorefrontPreview(url);
          onStorefrontImageChange(url);
          toast.success('Profile picture uploaded successfully');
        }
        setIsStorefrontLoading(false);
      } else {
        setIsCoverLoading(true);
        const url = await uploadImage(croppedFile, 'cover');
        if (url) {
          setCoverPreview(url);
          onCoverImageChange(url);
          toast.success('Storefront image uploaded successfully');
        }
        setIsCoverLoading(false);
      }
    } catch (error) {
      console.error("Error processing cropped image:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process the cropped image");
    } finally {
      // Reset cropper state
      setShowCropper(false);
      setCropperImage(null);
      setCropperType(null);
      setTempFile(null);
      setTempFileType('image/jpeg');
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropperImage(null);
    setCropperType(null);
    setTempFile(null);
  };

  const handleRemoveStorefront = async () => {
    if (storefrontImageUrl) {
      const success = await deleteImage(storefrontImageUrl);
      if (success) {
        setStorefrontPreview(null);
        onStorefrontImageChange(null);
        
        // If using storefront as cover, also clear cover image
        if (useStorefrontAsCover) {
          onUseStorefrontAsCoverChange(false);
        }
        
        toast.success('Profile picture removed');
      }
    }
  };

  const handleRemoveCover = async () => {
    if (coverImageUrl) {
      const success = await deleteImage(coverImageUrl);
      if (success) {
        setCoverPreview(null);
        onCoverImageChange(null);
        toast.success('Storefront image removed');
      }
    }
  };

  const handleToggleUseStorefrontAsCover = (checked: boolean) => {
    onUseStorefrontAsCoverChange(checked);
    
    // If toggling on and we have a storefront image but no cover image
    if (checked && storefrontImageUrl && !coverImageUrl) {
      toast.success('Using profile picture as storefront image');
    }
  };

  // Simplified preview components
  const ProfilePreview = ({ url }: { url: string }) => (
    <div className="relative rounded-md overflow-hidden border border-gray-200">
      <img
        src={url}
        alt="Profile Picture"
        className="w-full aspect-square object-cover"
        loading="eager"
      />
      {isStorefrontLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );

  const CoverPreview = ({ url }: { url: string }) => (
    <div className="relative rounded-md overflow-hidden border border-gray-200">
      <img
        src={url}
        alt="Storefront Image"
        className="w-full aspect-video object-cover"
        loading="eager"
      />
      {isCoverLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Image Cropper Dialog */}
      {showCropper && cropperImage && (
        <ImageCropper
          imageSrc={cropperImage}
          aspectRatio={cropperType === 'profile' ? PROFILE_ASPECT_RATIO : COVER_ASPECT_RATIO}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          open={showCropper}
          imageType={tempFileType}
        />
      )}
      
      {/* Storefront Image (Required) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            Profile Picture <span className="text-red-500">*</span>
          </Label>
          {storefrontPreview && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemoveStorefront}
              disabled={disabled || isUploading || isStorefrontLoading}
              className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              Remove
            </Button>
          )}
        </div>
        <div className="text-xs text-gray-500 mb-2">
          This square image appears on your store profile and search results. 
          It will be displayed as a 1:1 aspect ratio image (40px × 40px).
        </div>
        
        {storefrontPreview ? (
          <ProfilePreview url={storefrontPreview} />
        ) : (
          <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {isUploading || isStorefrontLoading ? (
                <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
              ) : (
                <ImagePlus className="h-8 w-8 text-gray-400 mb-2" />
              )}
              <p className="text-sm text-gray-500">
                {isUploading || isStorefrontLoading ? "Uploading..." : "Click to upload profile picture"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PNG, JPG, WebP or GIF (max. 5MB)
              </p>
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'profile')}
              disabled={disabled || isUploading || isStorefrontLoading}
            />
          </label>
        )}
      </div>

      {/* Use Storefront as Cover Toggle */}
      <div className="flex items-center space-x-2">
        <Switch
          id="use-storefront-as-cover"
          checked={useStorefrontAsCover}
          onCheckedChange={handleToggleUseStorefrontAsCover}
          disabled={disabled || !storefrontPreview}
        />
        <Label htmlFor="use-storefront-as-cover" className="text-sm">
          Use profile picture as storefront image
        </Label>
      </div>

      {/* Cover Image (Optional) - Only show if not using storefront as cover */}
      {!useStorefrontAsCover && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              Storefront Image <span className="text-gray-400">(Optional)</span>
            </Label>
            {coverPreview && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveCover}
                disabled={disabled || isUploading || isCoverLoading}
                className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                Remove
              </Button>
            )}
          </div>
          <div className="text-xs text-gray-500 mb-2">
            This wide image will be displayed at the top of your store profile page with a 16:9 aspect ratio.
          </div>
          
          {coverPreview ? (
            <CoverPreview url={coverPreview} />
          ) : (
            <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {isUploading || isCoverLoading ? (
                  <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                ) : (
                  <ImagePlus className="h-8 w-8 text-gray-400 mb-2" />
                )}
                <p className="text-sm text-gray-500">
                  {isUploading || isCoverLoading ? "Uploading..." : "Click to upload storefront image"}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG, WebP or GIF (max. 5MB)
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => handleFileSelect(e, 'cover')}
                disabled={disabled || isUploading || isCoverLoading}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
} 